import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Sparkles, ListCheck, ChevronDown, ArrowRight, BookOpen, ScrollText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { getGenreInfo, getSubgenreInfo, getTopLevelGenreInfo } from '@/lib/utils/genre-utils';
import pathwaysHero from '@/assets/heroes/reading_pathways_hero_v2.png';

interface Props {
    passage: Passage;
    onGenreClick?: (genre: string) => void;
    onSubgenreClick?: (subgenre: string) => void;
    onFormClick?: (form: string) => void;
    structuralRole?: string;
    mode?: 'standalone' | 'hero';
}

export default function PassageGenreGuide({
    passage,
    onGenreClick,
    onSubgenreClick,
    onFormClick,
    structuralRole,
    mode = 'standalone'
}: Props) {
    const [isStrategyExpanded, setIsStrategyExpanded] = useState(false);
    const [isDiscoveryExpanded, setIsDiscoveryExpanded] = useState(false);
    const [activeSection, setActiveSection] = useState<'genre' | 'subgenre' | 'form' | null>(null);

    const genreInfo = useMemo(() => {
        if (!passage?.genre || !passage?.subgenre || !passage?.form) return null;
        return getGenreInfo(passage.genre, passage.subgenre, passage.form);
    }, [passage]);

    const subgenreInfo = useMemo(() => {
        if (!passage?.genre || !passage?.subgenre) return null;
        return getSubgenreInfo(passage.genre, passage.subgenre);
    }, [passage]);

    const topLevelGenreInfo = useMemo(() => {
        if (!passage?.genre) return null;
        return getTopLevelGenreInfo(passage.genre);
    }, [passage]);

    if (!passage?.genre || !passage?.subgenre || !passage?.form) {
        return null;
    }

    const isHero = mode === 'hero';

    const toggleSection = (section: 'genre' | 'subgenre' | 'form') => {
        if (activeSection === section) {
            setActiveSection(null);
            setIsDiscoveryExpanded(false);
        } else {
            setActiveSection(section);
            setIsDiscoveryExpanded(true);
        }
    };

    const Content = (
        <div className={cn("relative z-10 space-y-6", isHero ? "w-full" : "")}>
            {/* Header for standalone mode */}
            {!isHero && (
                <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-primary/60">
                        Literary Framework
                    </span>
                    <div className="h-px w-12 bg-primary/20" />
                </div>
            )}

            {/* Summary sentence - Only in standalone mode. Hero mode summary is handled by parent. */}
            {!isHero && (
                <p className="text-xl md:text-3xl font-serif leading-relaxed italic text-foreground/90">
                    {structuralRole || genreInfo?.short_description}
                </p>
            )}

            {/* Path Layer: 3-wide on mobile, flex on desktop */}
            <div className="grid grid-cols-3 sm:flex sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                <button
                    onClick={() => toggleSection('genre')}
                    className={cn(
                        "group/btn relative flex-1 p-2.5 sm:p-4 rounded-xl sm:rounded-2xl border transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-left min-w-0",
                        isHero
                            ? "bg-black/30 border-white/20 hover:bg-black/40 backdrop-blur-md"
                            : "bg-primary/5 border-primary/10 hover:bg-primary/10",
                        activeSection === 'genre' && isHero && "ring-2 ring-white/20 bg-black/50 shadow-lg",
                        activeSection === 'genre' && !isHero && "ring-2 ring-primary/20 bg-primary/10"
                    )}
                >
                    <div className="flex flex-col gap-0.5 sm:gap-1 overflow-hidden">
                        <span className={cn(
                            "text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.1em] sm:tracking-[0.2em] truncate",
                            isHero ? "text-white/60" : "text-primary/40"
                        )}>Genre</span>
                        <div className="flex items-center justify-between gap-1 sm:gap-2">
                            <span className={cn(
                                "text-xs sm:text-lg font-serif font-medium truncate",
                                isHero ? "text-white drop-shadow-sm" : "text-foreground"
                            )}>{passage.genre}</span>
                            <div className={cn(
                                "p-1 sm:p-1.5 rounded-full transition-colors shrink-0",
                                isHero ? "bg-white/10 group-hover/btn:bg-white/20" : "bg-primary/10 group-hover/btn:bg-primary/20",
                                activeSection === 'genre' && (isHero ? "bg-white/20" : "bg-primary/20")
                            )}>
                                <ChevronDown className={cn("w-3 h-3 sm:w-3.5 sm:h-3.5 transition-transform duration-300", activeSection === 'genre' && "rotate-180")} />
                            </div>
                        </div>
                    </div>
                </button>

                <button
                    onClick={() => toggleSection('subgenre')}
                    className={cn(
                        "group/btn relative flex-1 p-2.5 sm:p-4 rounded-xl sm:rounded-2xl border transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-left min-w-0",
                        isHero
                            ? "bg-black/30 border-white/20 hover:bg-black/40 backdrop-blur-md"
                            : "bg-primary/5 border-primary/10 hover:bg-primary/10",
                        activeSection === 'subgenre' && isHero && "ring-2 ring-white/20 bg-black/50 shadow-lg",
                        activeSection === 'subgenre' && !isHero && "ring-2 ring-primary/20 bg-primary/10"
                    )}
                >
                    <div className="flex flex-col gap-0.5 sm:gap-1 overflow-hidden">
                        <span className={cn(
                            "text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.1em] sm:tracking-[0.2em] truncate",
                            isHero ? "text-white/60" : "text-primary/40"
                        )}>Subgenre</span>
                        <div className="flex items-center justify-between gap-1 sm:gap-2">
                            <span className={cn(
                                "text-xs sm:text-lg font-serif font-medium truncate",
                                isHero ? "text-white drop-shadow-sm" : "text-foreground"
                            )}>{passage.subgenre}</span>
                            <div className={cn(
                                "p-1 sm:p-1.5 rounded-full transition-colors shrink-0",
                                isHero ? "bg-white/10 group-hover/btn:bg-white/20" : "bg-primary/10 group-hover/btn:bg-primary/20",
                                activeSection === 'subgenre' && (isHero ? "bg-white/20" : "bg-primary/20")
                            )}>
                                <ChevronDown className={cn("w-3 h-3 sm:w-3.5 sm:h-3.5 transition-transform duration-300", activeSection === 'subgenre' && "rotate-180")} />
                            </div>
                        </div>
                    </div>
                </button>

                <button
                    onClick={() => toggleSection('form')}
                    className={cn(
                        "group/btn relative flex-1 p-2.5 sm:p-4 rounded-xl sm:rounded-2xl border transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-left min-w-0",
                        isHero
                            ? "bg-black/30 border-white/20 hover:bg-black/40 backdrop-blur-md"
                            : "bg-primary/5 border-primary/10 hover:bg-primary/10",
                        activeSection === 'form' && isHero && "ring-2 ring-white/20 bg-black/50 shadow-lg",
                        activeSection === 'form' && !isHero && "ring-2 ring-primary/20 bg-primary/10"
                    )}
                >
                    <div className="flex flex-col gap-0.5 sm:gap-1 overflow-hidden">
                        <span className={cn(
                            "text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.1em] sm:tracking-[0.2em] truncate",
                            isHero ? "text-white/60" : "text-primary/40"
                        )}>
                            <span className="inline sm:hidden">Form</span>
                            <span className="hidden sm:inline">Literary Form</span>
                        </span>
                        <div className="flex items-center justify-between gap-1 sm:gap-2">
                            <span className={cn(
                                "text-xs sm:text-lg font-serif font-medium truncate",
                                isHero ? "text-white drop-shadow-sm" : "text-foreground"
                            )}>{passage.form}</span>
                            <div className={cn(
                                "p-1 sm:p-1.5 rounded-full transition-colors shrink-0",
                                isHero ? "bg-white/10 group-hover/btn:bg-white/20" : "bg-primary/10 group-hover/btn:bg-primary/20",
                                activeSection === 'form' && (isHero ? "bg-white/20" : "bg-primary/20")
                            )}>
                                <ChevronDown className={cn("w-3 h-3 sm:w-3.5 sm:h-3.5 transition-transform duration-300", activeSection === 'form' && "rotate-180")} />
                            </div>
                        </div>
                    </div>
                </button>
            </div>

            {/* 3. Definition Layer: Levels of Literary Artistry (Collapsible) */}
            <AnimatePresence>
                {isDiscoveryExpanded && activeSection && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="pt-4 pb-2">
                            {/* Selected Detail Card */}
                            <div className={cn(
                                "space-y-4 p-6 rounded-2xl border transition-all duration-300",
                                isHero
                                    ? "bg-black/40 border-white/20 hover:bg-black/50 backdrop-blur-md"
                                    : "bg-background/40 border-border/20 backdrop-blur-sm hover:bg-background/60"
                            )}>
                                <div className="flex items-center justify-between">
                                    <span className={cn(
                                        "text-[9px] font-bold uppercase tracking-widest",
                                        isHero ? "text-white/60" : "text-primary/60"
                                    )}>
                                        {activeSection === 'genre' ? 'Macro Genre' : activeSection === 'subgenre' ? 'Subgenre' : 'Literary Form'}
                                    </span>
                                    <Badge variant="secondary" className={cn(
                                        "text-[10px] px-2 py-0 border-none",
                                        isHero ? "bg-white/10 text-white" : "bg-primary/10 text-primary"
                                    )}>
                                        {activeSection === 'genre' ? passage.genre : activeSection === 'subgenre' ? passage.subgenre : passage.form}
                                    </Badge>
                                </div>
                                <button
                                    onClick={() => {
                                        if (activeSection === 'genre') onGenreClick?.(passage.genre);
                                        else if (activeSection === 'subgenre') onSubgenreClick?.(passage.subgenre);
                                        else onFormClick?.(passage.form);
                                    }}
                                    className="text-left group/desc w-full"
                                >
                                    <p className={cn(
                                        "text-base leading-relaxed font-light italic transition-colors",
                                        isHero ? "text-white/90 group-hover/desc:text-white" : "text-foreground/80 group-hover/desc:text-primary"
                                    )}>
                                        {activeSection === 'genre' ? topLevelGenreInfo?.long_description : activeSection === 'subgenre' ? subgenreInfo?.long_description : genreInfo?.long_description}
                                    </p>
                                </button>
                                {((activeSection === 'genre' && onGenreClick) || (activeSection === 'subgenre' && onSubgenreClick) || (activeSection === 'form' && onFormClick)) && (
                                    <button
                                        onClick={() => {
                                            if (activeSection === 'genre') onGenreClick?.(passage.genre);
                                            else if (activeSection === 'subgenre') onSubgenreClick?.(passage.subgenre);
                                            else onFormClick?.(passage.form);
                                        }}
                                        className={cn(
                                            "flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors mt-auto pt-2",
                                            isHero ? "text-white/50 hover:text-white" : "text-primary/40 hover:text-primary"
                                        )}
                                    >
                                        Explore Other {activeSection === 'genre' ? passage.genre : activeSection === 'subgenre' ? passage.subgenre : passage.form} Passages <ArrowRight className="h-3 w-3" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    return (
        <div className={cn("space-y-8", isHero && "space-y-4")}>
            {!isHero ? (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden p-8 md:p-10 rounded-[2.5rem] border border-border/40 bg-card/10 backdrop-blur-md shadow-[0_0_50px_rgba(0,0,0,0.05)] group/genre"
                >
                    {/* Background Illustration */}
                    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                        <div
                            className="absolute inset-0 opacity-[0.25] saturate-[0.8] grayscale-[0.2] transition-transform duration-1000 group-hover/genre:scale-105"
                            style={{
                                backgroundImage: `url(${pathwaysHero})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0) 100%)',
                                WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0) 100%)'
                            }}
                        />
                    </div>

                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 p-8 opacity-5 z-10 pointer-events-none">
                        <BookOpen className="w-40 h-40" />
                    </div>

                    {Content}
                </motion.div>
            ) : Content}

            {/* Reading Strategy Section */}
            <div className={cn("flex flex-col", isHero && "mt-1")}>
                <button
                    onClick={() => setIsStrategyExpanded(!isStrategyExpanded)}
                    className={cn(
                        "flex items-center justify-between w-full p-5 sm:p-8 rounded-2xl sm:rounded-3xl transition-all duration-300 group/strategy border relative overflow-hidden",
                        isHero
                            ? "bg-black/30 border-white/20 hover:bg-black/40 backdrop-blur-md p-4 sm:p-6"
                            : "bg-primary/[0.02] border-primary/10 hover:bg-primary/[0.04] p-5 sm:p-8"
                    )}
                >
                    {/* Decorative Background Glow */}
                    <div className={cn(
                        "absolute -right-8 -top-8 w-32 h-32 blur-3xl opacity-0 group-hover/strategy:opacity-20 transition-opacity duration-700 pointer-events-none rounded-full",
                        isHero ? "bg-white" : "bg-primary"
                    )} />

                    <div className="flex items-center gap-3 sm:gap-6 relative z-10">
                        <div className={cn(
                            "p-2.5 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-500 group-hover/strategy:rotate-3 group-hover/strategy:scale-110 shadow-sm",
                            isHero ? "bg-white/20 border border-white/30" : "bg-white border border-primary/10 shadow-primary/5"
                        )}>
                            <ScrollText className={cn("h-5 w-5 sm:h-7 sm:w-7", isHero ? "text-white/80" : "text-primary/70")} strokeWidth={1.5} />
                        </div>
                        <div className="text-left space-y-0.5 sm:space-y-1.5 cursor-pointer overflow-hidden">
                            <div className="flex items-center gap-2">
                                <span className={cn(
                                    "text-[8px] sm:text-[10px] font-bold uppercase tracking-[0.25em] sm:tracking-[0.4em] truncate opacity-70",
                                    isHero ? "text-white" : "text-primary"
                                )}>Hermeneutic Path</span>
                                <div className={cn("h-px w-4 sm:w-8", isHero ? "bg-white/20" : "bg-primary/20")} />
                                <Sparkles className={cn("h-3 w-3", isHero ? "text-white/30" : "text-primary/40")} />
                            </div>
                            <h3 className={cn(
                                "text-lg sm:text-2xl font-serif font-medium truncate",
                                isHero ? "text-white/95" : "text-foreground/90"
                            )}>How to Read {passage.subgenre}</h3>
                        </div>
                    </div>
                    <div className={cn(
                        "h-8 w-8 sm:h-12 sm:w-12 rounded-full border flex items-center justify-center transition-all duration-300 shrink-0 relative z-10",
                        isHero
                            ? "border-white/20 group-hover/strategy:bg-white/10 group-hover/strategy:border-white/40"
                            : "border-primary/10 group-hover/strategy:bg-primary/5 group-hover/strategy:border-primary/20"
                    )}>
                        <ChevronDown className={cn(
                            "h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-500 ease-[0.22,1,0.36,1]",
                            isHero ? "text-white/60" : "text-primary/50",
                            isStrategyExpanded && "rotate-180"
                        )} />
                    </div>
                </button>

                <AnimatePresence>
                    {isStrategyExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0, y: -20 }}
                            animate={{ height: 'auto', opacity: 1, y: 0 }}
                            exit={{ height: 0, opacity: 0, y: -20 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                        >
                            <div className={cn(
                                "p-6 sm:p-12 space-y-10 sm:space-y-20 rounded-[1.5rem] sm:rounded-[2.5rem] border border-dashed relative overflow-hidden",
                                isHero
                                    ? "bg-white/[0.03] border-white/20"
                                    : "bg-primary/[0.02] border-primary/20"
                            )}>
                                {/* Inner Gradient Mesh */}
                                <div className={cn(
                                    "absolute inset-0 opacity-10 pointer-events-none",
                                    isHero ? "bg-gradient-to-br from-white/20 via-transparent to-white/20" : "bg-gradient-to-br from-primary/10 via-transparent to-primary/10"
                                )} />

                                {/* Large Descriptive Strategy */}
                                {genreInfo?.interpreting_guidelines && (
                                    <div className="max-w-3xl space-y-4 sm:space-y-8 relative z-10">
                                        <div className="flex items-center gap-3">
                                            <div className={cn("p-1.5 rounded-md", isHero ? "bg-white/10" : "bg-primary/10")}>
                                                <ListCheck className={cn("h-4 w-4", isHero ? "text-white/70" : "text-primary/70")} />
                                            </div>
                                            <span className={cn(
                                                "text-[9px] sm:text-[11px] font-bold uppercase tracking-[0.25em]",
                                                isHero ? "text-white/60" : "text-primary/60"
                                            )}>Interpretive Lens</span>
                                        </div>
                                        <p className={cn(
                                            "text-lg sm:text-3xl font-serif italic leading-relaxed font-light",
                                            isHero ? "text-white/90" : "text-foreground/90"
                                        )}>
                                            "{genreInfo.interpreting_guidelines}"
                                        </p>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-20 relative z-10">
                                    {/* Key Principles */}
                                    {genreInfo?.key_characteristics && genreInfo.key_characteristics.length > 0 && (
                                        <div className="space-y-6 sm:space-y-8">
                                            <div className="flex items-center gap-3">
                                                <div className={cn("h-6 sm:h-10 w-1 rounded-full", isHero ? "bg-green-400" : "bg-green-600")} />
                                                <span className={cn(
                                                    "text-[9px] sm:text-[11px] font-bold uppercase tracking-[0.3em]",
                                                    isHero ? "text-green-400/90" : "text-green-700/80"
                                                )}>Foundational Principles</span>
                                            </div>
                                            <ul className="space-y-4 sm:space-y-6">
                                                {genreInfo.key_characteristics.map((char, i) => (
                                                    <li key={i} className="flex gap-4 sm:gap-5 group/item items-start">
                                                        <div className={cn(
                                                            "mt-2 h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full rotate-45 transition-all duration-500 group-hover/item:rotate-[135deg] group-hover/item:scale-125",
                                                            isHero ? "bg-green-400/40" : "bg-green-600/30"
                                                        )} />
                                                        <span className={cn(
                                                            "text-sm sm:text-lg leading-relaxed font-light transition-colors",
                                                            isHero ? "text-white/70 group-hover/item:text-white/90" : "text-foreground/80 group-hover/item:text-foreground"
                                                        )}>{char}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Warnings */}
                                    {genreInfo?.common_pitfalls && genreInfo.common_pitfalls.length > 0 && (
                                        <div className="space-y-6 sm:space-y-8">
                                            <div className="flex items-center gap-3">
                                                <div className={cn("h-6 sm:h-10 w-1 rounded-full", isHero ? "bg-amber-400" : "bg-amber-600")} />
                                                <span className={cn(
                                                    "text-[9px] sm:text-[11px] font-bold uppercase tracking-[0.3em]",
                                                    isHero ? "text-amber-400/90" : "text-amber-700/80"
                                                )}>Interpretive Warnings</span>
                                            </div>
                                            <ul className="space-y-4 sm:space-y-6">
                                                {genreInfo.common_pitfalls.map((pitfall, i) => (
                                                    <li key={i} className="flex gap-4 sm:gap-5 group/item items-start">
                                                        <div className={cn(
                                                            "mt-2 h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full transition-all duration-500 group-hover/item:scale-125",
                                                            isHero ? "bg-amber-400/40" : "bg-amber-600/30"
                                                        )} />
                                                        <span className={cn(
                                                            "text-sm sm:text-lg leading-relaxed font-light transition-colors",
                                                            isHero ? "text-white/70 group-hover/item:text-white/90" : "text-foreground/80 group-hover/item:text-foreground"
                                                        )}>{pitfall}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* Primary Purpose Footer */}
                                {genreInfo?.primary_purpose && (
                                    <div className={cn(
                                        "pt-8 sm:pt-14 border-t flex flex-col md:flex-row gap-5 sm:gap-8 md:items-center relative z-10",
                                        isHero ? "border-white/10" : "border-primary/10"
                                    )}>
                                        <div className="flex items-center gap-3 shrink-0">
                                            <div className={cn("p-2.5 rounded-full", isHero ? "bg-white/10" : "bg-primary/5")}>
                                                <Sparkles className={cn("h-4 w-4 sm:h-5 sm:w-5", isHero ? "text-white/50" : "text-primary/50")} />
                                            </div>
                                            <span className={cn(
                                                "text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em]",
                                                isHero ? "text-white/50" : "text-muted-foreground/70"
                                            )}>Prophetic Intention</span>
                                        </div>
                                        <p className={cn(
                                            "text-xs sm:text-base font-serif italic border-l md:border-l-0 md:pl-0 pl-6 leading-relaxed",
                                            isHero ? "text-white/60 border-white/20" : "text-muted-foreground border-primary/20"
                                        )}>
                                            {genreInfo.primary_purpose}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
